import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { match, P } from 'ts-pattern';
import { toSignal } from '@angular/core/rxjs-interop';
import { injectTranslation } from '@skylab-angular-shared/core';
import { IconComponent, TooltipDirective } from '@skylab-angular-shared/ui';
import { AnnouncementItem, AnnouncementsService } from './hintbox/announcements.service';
import { AnnouncementsDrawerComponent } from './hintbox/announcements-drawer.component';

@Component({
	selector: 'skl-announcements-button',
	template: `
		<button
			class="announcement-bell"
			(click)="announcementsDrawer.open()"
			[tooltip]="announcementTooltip()"
		>
			<div
				class="announcement-bell-wrapper" 
				[attr.data-notification-badge]="counter()"
			>
				<div class="announcement-bell-icon">
					<skl-icon name="notifications" size="30px" />
				</div>
			</div>
		</button>

		<skl-announcements-drawer #announcementsDrawer />
	`,
	styles: `
		:host {
			.announcement-bell {
				display: grid;
				place-content: center;
				cursor: pointer;
				height: 3.25rem;
				aspect-ratio: 1;

				.announcement-bell-icon {
					--icon-color: white;

					border-radius: 30px;
					overflow: clip;
					width: 1.75rem;
					height: 1.75rem;
					transition: corner-top-right-shape 0.3s ease;
				}

				.announcement-bell-wrapper {
					position: relative;

					&[data-notification-badge]:not([data-notification-badge='']):not([data-notification-badge='0']) {
						.announcement-bell-icon {
							corner-top-right-shape: scoop;
						}
					}

					&::after {
						left: 1.0625rem;
						right: auto;
					}
				}
			}
		}
	`,
	imports: [TooltipDirective, AnnouncementsDrawerComponent, IconComponent],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class AnnouncementsButtonComponent {
	protected readonly _T = injectTranslation();
	readonly #announcementsService = inject(AnnouncementsService);

	public readonly announcements = toSignal<AnnouncementItem[]>(this.#announcementsService.announcements$);

	public readonly counter = computed(() => {
		const amount = this.announcements()?.length ?? 0;

		if (amount <= 0) {
			return '';
		}

		const max = 99;

		return amount > max ? `${max}+` : amount;
	});

	public readonly announcementTooltip = computed(() => {
		const amount = this.announcements()?.length ?? 0;

		return match(amount)
			.with(0, () => '')
			.with(1, () => `${amount} ${this._T('app-bar.announcements.one')}`)
			.with(
				P.when((n) => (n >= 5 && n <= 21) || n % 10 === 1),
				() => `${amount} ${this._T('app-bar.announcements.from-5-to-21')}`,
			)
			.with(
				P.when((n) => [2, 3, 4].includes(n % 10)),
				() => `${amount} ${this._T('app-bar.announcements.ends-with-2-or-3-or-4')}`,
			)
			.otherwise(() => '');
	});
}
